<div class="space-y-4">
    <!-- Top KPI Metrics for Timeline -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <!-- 1. Total Stays -->
        <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col justify-between">
            <span class="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Total Stays</span>
            <div class="text-xl font-bold text-slate-900 mt-1">{{ totalStaysCount() }}</div>
            <span class="text-[10px] text-slate-400">In timeline scope</span>
        </div>

        <!-- 2. Peak Occupancy -->
        <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col justify-between">
            <span class="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Peak Occupancy</span>
            <div class="text-xl font-bold text-slate-900 mt-1">
                {{ peakOccupancy() }} / {{ capacity() }} <span class="text-xs font-normal text-slate-500">cars</span>
            </div>
            <span class="text-[10px] font-medium"
                  [class.text-amber-700]="peakOccupancy() > capacity()"
                  [class.text-indigo-600]="peakOccupancy() === capacity()"
                  [class.text-slate-500]="peakOccupancy() < capacity()">
                {{ peakOccupancy() > capacity() ? 'Exceeded (+ ' + (peakOccupancy() - capacity()) + ' excess)' : (peakOccupancy() === capacity() ? 'At full capacity' : 'Within capacity') }}
            </span>
        </div>

        <!-- 3. Times Overbooked -->
        <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col justify-between">
            <span class="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Times Overbooked</span>
            <div class="text-xl font-bold text-slate-900 mt-1">
                {{ totalTimesOverbooked() }}
            </div>
            <span class="text-[10px] font-medium"
                  [class.text-amber-700]="totalTimesOverbooked() > 0"
                  [class.text-slate-500]="totalTimesOverbooked() === 0">
                {{ totalTimesOverbooked() > 0 ? 'Recorded incidents' : 'None' }}
            </span>
        </div>

        <!-- 4. Duration Overbooked -->
        <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col justify-between">
            <span class="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Time Overbooked</span>
            <div class="text-xl font-bold text-slate-900 mt-1">
                {{ formattedTotalTimeOverbooked() }}
            </div>
            <span class="text-[10px] text-slate-500 font-medium">
                Total duration
            </span>
        </div>
    </div>

    <!-- Chart Card -->
    <div class="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4 relative overflow-hidden">
        <!-- Header & Legend & Zoom Switcher -->
        <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <div>
                <h3 class="text-sm font-bold text-slate-900">Pool Capacity & Usage Timeline</h3>
                <p class="text-xs text-slate-500 mt-0.5">Timeline of concurrent vehicles parked vs maximum allowed capacity.</p>
            </div>

            <div class="flex flex-wrap items-center gap-4">
                <!-- View Range Presets -->
                <div class="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 text-xs font-semibold">
                    <button type="button"
                            class="px-2.5 py-1 rounded-md transition-all cursor-pointer"
                            [class.bg-white]="viewPreset() === 'fit'"
                            [class.shadow-2xs]="viewPreset() === 'fit'"
                            [class.text-indigo-600]="viewPreset() === 'fit'"
                            [class.text-slate-600]="viewPreset() !== 'fit'"
                            (click)="setPreset('fit')"
                            title="Auto-focus around activity with padding">
                        Auto Fit
                    </button>
                    <button type="button"
                            class="px-2.5 py-1 rounded-md transition-all cursor-pointer"
                            [class.bg-white]="viewPreset() === '24h'"
                            [class.shadow-2xs]="viewPreset() === '24h'"
                            [class.text-indigo-600]="viewPreset() === '24h'"
                            [class.text-slate-600]="viewPreset() !== '24h'"
                            (click)="setPreset('24h')">
                        24h
                    </button>
                    <button type="button"
                            class="px-2.5 py-1 rounded-md transition-all cursor-pointer"
                            [class.bg-white]="viewPreset() === '7d'"
                            [class.shadow-2xs]="viewPreset() === '7d'"
                            [class.text-indigo-600]="viewPreset() === '7d'"
                            [class.text-slate-600]="viewPreset() !== '7d'"
                            (click)="setPreset('7d')">
                        7 Days
                    </button>
                    <button type="button"
                            class="px-2.5 py-1 rounded-md transition-all cursor-pointer"
                            [class.bg-white]="viewPreset() === 'all'"
                            [class.shadow-2xs]="viewPreset() === 'all'"
                            [class.text-indigo-600]="viewPreset() === 'all'"
                            [class.text-slate-600]="viewPreset() !== 'all'"
                            (click)="setPreset('all')">
                        Full Range
                    </button>
                </div>

                <!-- Legend -->
                <div class="flex flex-wrap items-center gap-3 text-xs font-medium border-l border-slate-200 pl-3">
                    <div class="flex items-center gap-1.5">
                        <span class="w-3 h-2 rounded-xs bg-indigo-600"></span>
                        <span class="text-slate-600">Occupancy</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <span class="w-4 h-0.5 border-t-2 border-dashed border-rose-500"></span>
                        <span class="text-rose-700 font-semibold">Capacity ({{ capacity() }})</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <span class="w-3 h-2 rounded-xs bg-rose-200/80 border border-rose-300"></span>
                        <span class="text-rose-600">Overbooked</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Chart Main Area -->
        <div class="relative pt-8 pb-6 select-none">
            <!-- Active Scrubber Tooltip Overlay (shows time, status, peak, and license plates) -->
            @if (hoveredPoint(); as p) {
            <div class="absolute top-0 left-1/2 -translate-x-1/2 bg-slate-900/95 text-white text-xs px-4 py-2.5 rounded-xl shadow-2xl z-30 pointer-events-none backdrop-blur-md border border-slate-700/80 animate-in fade-in zoom-in-95 duration-100 max-w-[96%] sm:max-w-2xl lg:max-w-4xl space-y-2">
                <!-- Summary Bar: Timestamp, Occupancy, Status Badges -->
                <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
                    <!-- Timestamp -->
                    <div class="font-semibold text-slate-200 flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full"
                              [class.bg-rose-500]="p.occupancy > capacity()"
                              [class.bg-amber-400]="p.occupancy === capacity()"
                              [class.bg-emerald-400]="p.occupancy < capacity()"></span>
                        {{ p.time | date:'medium' }}
                    </div>

                    <!-- Occupancy vs Capacity -->
                    <div class="font-bold text-slate-200"
                         [class.text-rose-400]="p.occupancy > capacity()"
                         [class.text-amber-300]="p.occupancy === capacity()"
                         [class.text-emerald-400]="p.occupancy < capacity()">
                        {{ p.occupancy }} / {{ capacity() }} cars ({{ ((p.occupancy / capacity()) * 100).toFixed(0) }}%)
                    </div>

                    <!-- Status Badges -->
                    <div class="flex items-center gap-1.5">
                        @if (p.occupancy > capacity()) {
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500 text-white flex items-center gap-1 shadow-2xs">
                            ⚠️ OVERBOOKED (+{{ p.occupancy - capacity() }})
                        </span>
                        } @else if (p.occupancy === capacity()) {
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-white shadow-2xs">
                            AT CAPACITY
                        </span>
                        } @else {
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600/80 text-white shadow-2xs">
                            NORMAL
                        </span>
                        }

                        @if (p.isPeak && p.occupancy > 0) {
                        <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-500 text-white">
                            ★ PEAK
                        </span>
                        }
                    </div>
                </div>

                <!-- License Plates Breakdown (Covered vs Overbooked) -->
                @if (p.activePlates.length > 0) {
                <div class="pt-2 border-t border-slate-700/80 space-y-1.5">
                    <!-- Overbooked / Overflow Plates -->
                    @if (p.overflowPlates.length > 0) {
                    <div class="flex flex-wrap items-center gap-1.5">
                        <span class="text-[10px] font-bold uppercase text-rose-400 tracking-wider">Overbooked ({{ p.overflowPlates.length }}):</span>
                        @for (pl of p.overflowPlates; track pl) {
                        <span class="px-1.5 py-0.5 rounded text-[11px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40">
                            {{ pl }}
                        </span>
                        }
                    </div>
                    }

                    <!-- Covered Plates -->
                    @if (p.coveredPlates.length > 0) {
                    <div class="flex flex-wrap items-center gap-1.5">
                        <span class="text-[10px] font-bold uppercase text-indigo-300 tracking-wider">Covered ({{ p.coveredPlates.length }}):</span>
                        @for (pl of p.coveredPlates; track pl) {
                        <span class="px-1.5 py-0.5 rounded text-[11px] font-mono font-medium bg-indigo-500/20 text-indigo-200 border border-indigo-500/30">
                            {{ pl }}
                        </span>
                        }
                    </div>
                    }
                </div>
                }
            </div>
            }

            <!-- SVG & Grid Container -->
            <div class="relative w-full h-64 border-b border-l border-slate-200 bg-slate-50/30 overflow-hidden rounded-bl-sm">
                <!-- Vertical Time Grid Lines -->
                @for (m of timeAxisMarks(); track m.xPct) {
                <div class="absolute top-0 bottom-0 border-l border-slate-200/60 pointer-events-none z-0"
                     [style.left.%]="m.xPct">
                </div>
                }

                <!-- Highlight Bands for Overbooked Periods (Soft translucent indicator) -->
                @for (f of overbookedPeriods(); track f.startTime.getTime()) {
                <div class="absolute top-0 bottom-0 bg-rose-500/10 border-l border-r border-rose-400/40 pointer-events-none z-0"
                     [style.left.%]="f.xStartPct"
                     [style.width.%]="f.xWidthPct"
                     [title]="'Overbooked from ' + formatTime(f.startTime) + ' to ' + formatTime(f.endTime) + ' (' + f.durationMinutes + ' min)'">
                    <span class="absolute top-2 left-1.5 text-[9px] font-bold text-slate-700 bg-white/95 px-1.5 py-0.5 rounded-md border border-slate-200 shadow-2xs">
                        Overbooked ({{ f.durationMinutes }}m)
                    </span>
                </div>
                }

                <!-- Horizontal Capacity Threshold Line -->
                <div class="absolute left-0 right-0 border-b-2 border-dashed border-rose-400/80 z-10 pointer-events-none"
                     [style.top.%]="capacityYPosPct()">
                    <span class="absolute right-2 -top-5 text-[10px] font-bold text-slate-700 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-2xs">
                        Max Capacity: {{ capacity() }}
                    </span>
                </div>

                <!-- SVG Curve, Area & Point Markers -->
                <svg viewBox="0 0 800 240" preserveAspectRatio="none" class="w-full h-full relative z-10">
                    <defs>
                        <linearGradient id="occupancyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stop-color="#4f46e5" stop-opacity="0.25" />
                            <stop offset="100%" stop-color="#4f46e5" stop-opacity="0.01" />
                        </linearGradient>
                    </defs>

                    <!-- Shaded Area Under Line -->
                    @if (areaPathSvg()) {
                    <polygon [attr.points]="areaPathSvg()" fill="url(#occupancyGradient)" />
                    }

                    <!-- Step Line -->
                    @if (linePathSvg()) {
                    <polyline [attr.points]="linePathSvg()" fill="none" stroke="#4338ca" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
                    }
                </svg>

                <!-- Continuous Interactive Mouse Overlay across Timeline -->
                <div class="absolute inset-0 z-20 cursor-crosshair"
                     (mousemove)="onChartMouseMove($event)"
                     (mouseleave)="onChartMouseLeave()">
                    <!-- Scrubber Guideline following mouse X -->
                    @if (hoverX() !== null) {
                    <div class="absolute top-0 bottom-0 border-l-2 border-indigo-600 pointer-events-none z-20"
                         [style.left.%]="hoverX()">
                        <div class="w-2.5 h-2.5 rounded-full bg-indigo-600 -ml-[4px] shadow-sm ring-2 ring-white"
                             [style.top.%]="hoveredPoint()?.yPct ?? 100"></div>
                    </div>
                    }
                </div>
            </div>

            <!-- X-Axis Marks -->
            <div class="relative w-full h-8 mt-2">
                @for (m of timeAxisMarks(); track m.xPct) {
                <div class="absolute text-slate-500 leading-tight"
                     [style.left.%]="m.xPct"
                     [style.transform]="m.align === 'left' ? 'none' : (m.align === 'right' ? 'translateX(-100%)' : 'translateX(-50%)')"
                     [style.text-align]="m.align">
                    <div class="text-[10px] font-bold text-slate-700">{{ m.timeLabel }}</div>
                    <div class="text-[9px] text-slate-400 font-medium">{{ m.dateLabel }}</div>
                </div>
                }
            </div>
        </div>

        <!-- Overbooked Periods & Related License Plates Section Below Timeline -->
        @if (overbookedPeriods().length > 0) {
        <div class="pt-5 border-t border-slate-200/80 space-y-3.5">
            <!-- Header Strip -->
            <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div class="flex items-center gap-2">
                        <h4 class="text-sm font-bold text-slate-900">
                            Overbooked Periods & Related License Plates
                        </h4>
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            {{ overbookedPeriods().length }} {{ overbookedPeriods().length === 1 ? 'incident' : 'incidents' }}
                        </span>
                        <span class="text-xs font-medium text-slate-500">
                            • {{ formattedTotalTimeOverbooked() }} total excess
                        </span>
                    </div>
                    <p class="text-xs text-slate-500 mt-0.5">
                        Detailed intervals when parked vehicles exceeded the pool capacity, showing excess and covered license plates.
                    </p>
                </div>

                <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 shadow-2xs">
                    <span class="w-2 h-2 rounded-full bg-indigo-600"></span>
                    <span>Pool Capacity: <strong class="text-slate-900 font-semibold">{{ capacity() }} {{ capacity() === 1 ? 'car' : 'cars' }}</strong></span>
                </div>
            </div>

            <!-- Compact, Scannable Incident Table -->
            <div class="border border-slate-200 rounded-xl overflow-hidden shadow-2xs bg-white">
                <div class="overflow-x-auto">
                    <table class="w-full text-left text-xs">
                        <thead>
                            <tr class="bg-slate-50/80 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                <th class="py-2.5 px-4 font-semibold">Time Period</th>
                                <th class="py-2.5 px-3 font-semibold">Duration</th>
                                <th class="py-2.5 px-3 font-semibold">Peak Usage</th>
                                <th class="py-2.5 px-3 font-semibold">Overbooked Vehicle(s)</th>
                                <th class="py-2.5 px-3 font-semibold">Covered Vehicle(s)</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100">
                            @for (inc of overbookedPeriods(); track inc.startTime.getTime()) {
                            <tr class="hover:bg-slate-50/70 transition-colors">
                                <!-- Time Period -->
                                <td class="py-2.5 px-4 font-medium text-slate-800 whitespace-nowrap">
                                    <div class="flex items-center gap-1.5">
                                        @if (inc.isOngoing) {
                                        <span>{{ inc.startTime | date:'shortTime' }} – Active</span>
                                        <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">Now</span>
                                        } @else {
                                        <span>{{ inc.startTime | date:'shortTime' }} – {{ inc.endTime | date:'shortTime' }}</span>
                                        }
                                        <span class="text-[11px] text-slate-400 font-normal">({{ inc.startTime | date:'mediumDate' }})</span>
                                    </div>
                                </td>

                                <!-- Duration -->
                                <td class="py-2.5 px-3 whitespace-nowrap">
                                    <span class="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold text-[11px]">
                                        {{ formatDuration(inc.durationMinutes) }}
                                    </span>
                                </td>

                                <!-- Peak Occupancy -->
                                <td class="py-2.5 px-3 whitespace-nowrap text-slate-700">
                                    <span class="font-bold text-slate-900">{{ inc.peakOccupancy }} / {{ capacity() }}</span>
                                    <span class="text-rose-600 font-semibold ml-1 text-[11px]">(+{{ inc.excessCars }} excess)</span>
                                </td>

                                <!-- Overbooked Plates -->
                                <td class="py-2.5 px-3">
                                    <div class="flex flex-wrap items-center gap-1.5">
                                        @for (plate of inc.overflowPlates; track plate) {
                                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                            🚗 {{ plate }}
                                            <span class="text-[9px] font-semibold text-rose-600 uppercase">Excess</span>
                                        </span>
                                        }
                                        @if (inc.overflowPlates.length === 0) {
                                        <span class="text-slate-400 text-xs">—</span>
                                        }
                                    </div>
                                </td>

                                <!-- Covered Plates -->
                                <td class="py-2.5 px-3">
                                    <div class="flex flex-wrap items-center gap-1.5">
                                        @for (plate of inc.coveredPlates; track plate) {
                                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200">
                                            🚗 {{ plate }}
                                            <span class="text-[9px] font-normal text-slate-400 uppercase">Covered</span>
                                        </span>
                                        }
                                        @if (inc.coveredPlates.length === 0) {
                                        <span class="text-slate-400 text-xs">—</span>
                                        }
                                    </div>
                                </td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        }

        <!-- All Overbooked Vehicles List (if any overbooked stays exist) -->
        @if (overbookedVehiclesSummary().length > 0) {
        <div class="pt-3 border-t border-slate-100">
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                <span>Vehicles Admitted Under Pool Overbooking ({{ overbookedVehiclesSummary().length }})</span>
            </h4>
            <div class="flex flex-wrap items-center gap-2">
                @for (v of overbookedVehiclesSummary(); track v.plate) {
                <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-xs">
                    <span class="font-mono font-bold text-slate-800">🚗 {{ v.plate }}</span>
                    <span class="text-slate-300">•</span>
                    <span class="text-slate-600">{{ v.startedAt | date:'shortTime' }}</span>
                    @if (v.isCurrentlyActive) {
                    <span class="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        Active Overflow
                    </span>
                    } @else {
                    <span class="text-slate-500 text-[11px]">({{ formatDuration(v.durationMinutes) }})</span>
                    }
                </div>
                }
            </div>
        </div>
        }
    </div>
</div>
